import { type } from 'arktype';

export const PlanInstanceStatus = {
  DRAFT: 'draft',
  PUBLISHED: 'published',
} as const;
export type PlanInstanceStatus = (typeof PlanInstanceStatus)[keyof typeof PlanInstanceStatus];

export const PLAN_PERIOD_BASELINE_STRATEGIES = ['template', 'copy', 'empty'] as const;

export type PlanPeriodBaselineStrategy = (typeof PLAN_PERIOD_BASELINE_STRATEGIES)[number];

/**
 * Per-employee override for a single shift unit on a specific date.
 * Each employee can independently override their own time/location for the
 * shift unit they are assigned to.
 */
export const employeeShiftOverrideSchema = type({
  'startTime?': type('/^([01]\\d|2[0-3]):[0-5]\\d$/').configure({
    description: 'Override start time for this employee on this unit/date (HH:mm).',
    examples: ['08:30'],
  }),
  'endTime?': type('/^([01]\\d|2[0-3]):[0-5]\\d$/').configure({
    description: 'Override end time for this employee on this unit/date (HH:mm).',
    examples: ['16:30'],
  }),
  'locationId?': type('string | null').configure({
    description:
      '@deprecated since WL-6 (2026-05). Work location is day-scoped and lives on the personal calendar (`location-override` slot). New writes set this to null or omit it; legacy values are read only as a migration source.',
    examples: ['location_abc123'],
  }),
}).configure({
  description: 'Per-employee time override for a single shift unit on a single date. (Legacy `locationId` field is deprecated.)',
});

export type EmployeeShiftOverride = typeof employeeShiftOverrideSchema.t;

/**
 * Per-shiftUnit override for a specific date.
 */
export const shiftUnitOverrideSchema = type({
  'closed?': type('boolean').configure({
    description: 'When true, this shift unit is not available on this date (e.g. holiday closure).',
    examples: [true],
  }),
  'capacity?': type('number>=0').configure({
    description: 'Override capacity for this date. Replaces the shift rule default.',
    examples: [2],
  }),
  'applicationOpen?': type('boolean').configure({
    description: 'When true, employees may self-apply to this shift unit on this date.',
    examples: [true],
  }),

  'swapStatus?': type("'looking_for_swap' | 'available_for_takeover'").configure({
    description:
      "Employee-initiated swap intent. 'looking_for_swap' means the employee is offering their shift in exchange for one, 'available_for_takeover' means they are giving it up with no return.",
  }),
  'swapEmployeeId?': type('string').configure({
    description: 'ID of the employee that marked the swap status (disambiguates when multiple employees are assigned to the unit).',
    examples: ['employee_abc123'],
  }),
  'employeeOverrides?': type({
    '[string]': employeeShiftOverrideSchema,
  }).configure({
    description:
      'Per-employee time/location overrides keyed by employeeId. Each assigned employee can override their own values independently.',
  }),

  'startTime?': type('/^([01]\\d|2[0-3]):[0-5]\\d$/').configure({
    description:
      'Unit-level start time override for this shift unit on this date (HH:mm). Applies to all employees in this unit unless overridden per-employee via assignmentTimes.',
    examples: ['08:00'],
  }),
  'endTime?': type('/^([01]\\d|2[0-3]):[0-5]\\d$/').configure({
    description:
      'Unit-level end time override for this shift unit on this date (HH:mm). Applies to all employees in this unit unless overridden per-employee via assignmentTimes.',
    examples: ['16:00'],
  }),
  'locationId?': type('string | null').configure({
    description:
      '@deprecated since WL-6 (2026-05). Work location is day-scoped and lives on the personal calendar. Read paths ignore this field for work-location resolution; preserved only for migration tooling.',
    examples: ['loc_winterthur'],
  }),

  'assignmentLocations?': type({ '[string]': 'string' }).configure({
    description:
      '@deprecated since WL-6 (2026-05). Work location is day-scoped and lives on the personal calendar. Read paths ignore this field for work-location resolution; preserved only for migration tooling.',
    examples: [{ employee_abc123: 'loc_winterthur' }],
  }),

  'assignmentTimes?': type({
    '[string]': type({
      'startTime?': type('/^([01]\\d|2[0-3]):[0-5]\\d$/').configure({
        description:
          'Employee-specific shift start time on this date (HH:mm). Falls through to unit-level startTime override or template default.',
        examples: ['08:30'],
      }),
      'endTime?': type('/^([01]\\d|2[0-3]):[0-5]\\d$/').configure({
        description:
          'Employee-specific shift end time on this date (HH:mm). Falls through to unit-level endTime override or template default.',
        examples: ['16:30'],
      }),
    }),
  }).configure({
    description:
      'Per-employee time override for this shift unit on this date. Keys are employeeIds, values are partial { startTime, endTime } pairs. ' +
      "When set, the employee's shift entry uses these times instead of any unit-level override or the shift template default. " +
      'Used by demos like Globetrotter where each consultant may have a different lunch break inside the same shift unit.',
    examples: [{ employee_abc123: { startTime: '08:30', endTime: '12:00' } }],
  }),
}).configure({
  description:
    'Per-date override for a single shift unit (closure, capacity, application window, swap intent, per-employee time/location edits).',
});

export type ShiftUnitOverride = typeof shiftUnitOverrideSchema.t;

/**
 * Assignment overrides for a specific date.
 * Maps employeeId → shiftUnitId[] (which shift units the employee is assigned to).
 *
 * When present under the reserved `__assignments__` key in a date override,
 * this is the authoritative concrete assignment snapshot for that date.
 *
 * @example
 * {
 *   "employee-uuid-1": ["shift-unit-uuid-1", "shift-unit-uuid-2"],
 *   "employee-uuid-2": ["shift-unit-uuid-3"]
 * }
 */
export type AssignmentOverrides = Record<string, string[]>;

/**
 * Reserved key for assignment overrides within a date's override map.
 * Uses double underscores to avoid collision with UUID-based shift unit IDs.
 */
export const ASSIGNMENT_OVERRIDES_KEY = '__assignments__' as const;

export const DEFAULT_SUPPRESSIONS_KEY = '__defaultSuppressions__' as const;

export type DefaultSuppressions = Record<string, string[]>;

export function getDefaultSuppressions(overrides: PlanInstanceOverrides, dateKey: string): string[] {
  const raw = (overrides as Record<string, unknown>)[DEFAULT_SUPPRESSIONS_KEY];
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return [];
  const suppressed = (raw as Record<string, unknown>)[dateKey];
  return Array.isArray(suppressed) ? (suppressed as string[]) : [];
}

export function addDefaultSuppression(overrides: PlanInstanceOverrides, dateKey: string, personId: string): PlanInstanceOverrides {
  const raw = ((overrides as Record<string, unknown>)[DEFAULT_SUPPRESSIONS_KEY] ?? {}) as DefaultSuppressions;
  const existing = raw[dateKey] ?? [];
  if (existing.includes(personId)) return overrides;
  const updated: DefaultSuppressions = { ...raw, [dateKey]: [...existing, personId] };
  return { ...overrides, [DEFAULT_SUPPRESSIONS_KEY]: updated } as PlanInstanceOverrides;
}

export function removeDefaultSuppression(overrides: PlanInstanceOverrides, dateKey: string, personId: string): PlanInstanceOverrides {
  const raw = ((overrides as Record<string, unknown>)[DEFAULT_SUPPRESSIONS_KEY] ?? {}) as DefaultSuppressions;
  const existing = raw[dateKey] ?? [];
  const remaining = existing.filter(id => id !== personId);
  const updated: DefaultSuppressions = { ...raw };
  if (remaining.length === 0) {
    delete updated[dateKey];
  } else {
    updated[dateKey] = remaining;
  }
  if (Object.keys(updated).length === 0) {
    const result = { ...(overrides as Record<string, unknown>) };
    delete result[DEFAULT_SUPPRESSIONS_KEY];
    return result as PlanInstanceOverrides;
  }
  return { ...overrides, [DEFAULT_SUPPRESSIONS_KEY]: updated } as PlanInstanceOverrides;
}

/**
 * Date-level overrides mapping shiftUnitId to its override,
 * with an optional `__assignments__` key for assignment overrides.
 */
export const dateOverridesSchema = type({
  '[string]': shiftUnitOverrideSchema, // shiftUnitId → override
}).configure({
  description:
    'Map of shiftUnitId to its per-date override. May also contain a reserved `__assignments__` key that replaces the weekday template expansion for the date.',
});

export type DateOverrides = typeof dateOverridesSchema.t;

/**
 * Runtime-friendly date override type that includes the optional __assignments__ key.
 */
export type DateOverridesWithAssignments = Record<string, ShiftUnitOverride> & {
  [ASSIGNMENT_OVERRIDES_KEY]?: AssignmentOverrides;
};

/**
 * Plan instance overrides structure.
 * Keys are ISO date strings (YYYY-MM-DD).
 *
 * Each date can contain:
 * - Shift unit metadata overrides (keyed by shiftUnitId): { closed?, capacity? }
 * - Assignment overrides (keyed by "__assignments__"): employeeId → shiftUnitId[]
 *
 * @example
 * {
 *   "2026-02-14": {
 *     "unit-1": { "closed": true },
 *     "unit-2": { "capacity": 1 },
 *     "__assignments__": {
 *       "employee-uuid-1": ["unit-1", "unit-2"],
 *       "employee-uuid-2": ["unit-3"]
 *     }
 *   },
 *   "2026-02-16": {
 *     "unit-1": { "capacity": 0 }
 *   }
 * }
 */
export const planInstanceOverridesSchema = type({
  '[/^\\d{4}-\\d{2}-\\d{2}$/]': dateOverridesSchema,
}).configure({
  description:
    'Per-date override map keyed by ISO calendar date (YYYY-MM-DD). Allows closing shift units, changing capacity, and replacing assignments for specific dates.',
});

/**
 * Strict type inferred from arktype schema.
 * Use PlanInstanceOverrides for runtime use where indexing is needed.
 */
export type PlanInstanceOverridesStrict = typeof planInstanceOverridesSchema.t;

/**
 * Runtime-friendly type for plan instance overrides.
 * This allows indexing with string keys while maintaining semantic meaning.
 *
 * Note: The `__assignments__` key stores AssignmentOverrides (Record<string, string[]>)
 * but is typed as ShiftUnitOverride here for backwards compatibility. Access it via
 * `getAssignmentOverrides()` helper or cast explicitly when needed.
 */
export type PlanInstanceOverrides = Record<string, Record<string, ShiftUnitOverride>>;

/**
 * Sparse patch over {@link planInstanceOverridesSchema}. Granularity is the top-level key:
 * a present key replaces that date's whole override map, a `null` key deletes it, and an
 * absent key is left untouched.
 */
export const planInstanceOverridesPatchSchema = type({
  '[/^\\d{4}-\\d{2}-\\d{2}$/]': dateOverridesSchema.or('null'),
}).configure({
  description:
    "Sparse per-date override patch keyed by ISO calendar date (YYYY-MM-DD). Each supplied key replaces that date's entire override map; a `null` value deletes the date; dates absent from the patch keep their stored value. Mutually exclusive with `overrides`.",
});

/** Runtime-friendly counterpart to {@link planInstanceOverridesPatchSchema}. */
export type PlanInstanceOverridesPatch = Record<string, Record<string, ShiftUnitOverride> | null>;

/**
 * Apply a sparse patch to a stored overrides map. Returns a new object; does not mutate.
 */
export function mergeOverridesPatch(base: PlanInstanceOverrides, patch: PlanInstanceOverridesPatch): PlanInstanceOverrides {
  const merged: PlanInstanceOverrides = { ...base };

  for (const [key, value] of Object.entries(patch)) {
    if (value === null) {
      delete merged[key];
    } else {
      merged[key] = value;
    }
  }

  return merged;
}

function isPlainRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function arraysDeepEqual(a: unknown[], b: unknown[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (!deepEqual(a[i], b[i])) return false;
  return true;
}

function recordsDeepEqual(a: Record<string, unknown>, b: Record<string, unknown>): boolean {
  const keys = Object.keys(a);
  if (keys.length !== Object.keys(b).length) return false;
  for (const k of keys) if (!Object.prototype.hasOwnProperty.call(b, k) || !deepEqual(a[k], b[k])) return false;
  return true;
}

function deepEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (Array.isArray(a) && Array.isArray(b)) return arraysDeepEqual(a, b);
  if (isPlainRecord(a) && isPlainRecord(b)) return recordsDeepEqual(a, b);
  return false;
}

/** Key-order-insensitive patch: absent-from-`next` is untouched; only an explicit `null` in `next[key]` signals a delete. */
export function diffOverridesPatch(base: PlanInstanceOverrides, next: PlanInstanceOverridesPatch): PlanInstanceOverridesPatch {
  const patch: PlanInstanceOverridesPatch = {};

  for (const key of Object.keys(next)) {
    const value = next[key];
    if (value === null) {
      if (key in base) patch[key] = null;
    } else if (!deepEqual(base[key], value)) {
      patch[key] = value;
    }
  }

  return patch;
}

function isAssignmentOverrides(v: unknown): v is AssignmentOverrides {
  return !!v && typeof v === 'object' && Object.values(v as object).every(Array.isArray);
}

/**
 * Helper to extract assignment overrides from a date's override map.
 * Returns undefined if no assignment overrides exist for that date.
 */
export function getAssignmentOverrides(overrides: PlanInstanceOverrides, dateKey: string): AssignmentOverrides | undefined {
  const dateOverride = overrides[dateKey];
  if (!dateOverride) return undefined;
  const raw = (dateOverride as Record<string, unknown>)[ASSIGNMENT_OVERRIDES_KEY];
  return isAssignmentOverrides(raw) ? raw : undefined;
}

/**
 * Helper to set assignment overrides for a specific date.
 * Returns a new overrides object with the assignment applied (does not mutate).
 */
export function setAssignmentOverrides(
  overrides: PlanInstanceOverrides,
  dateKey: string,
  assignments: AssignmentOverrides
): PlanInstanceOverrides {
  const dateEntry = overrides[dateKey] ?? {};
  return {
    ...overrides,
    [dateKey]: { ...dateEntry, [ASSIGNMENT_OVERRIDES_KEY]: assignments } as Record<string, ShiftUnitOverride>,
  };
}

/**
 * Raw target-only assignment cleanup for person deletion (PDE-AC-15/17/18).
 * Removes the given employees from `__assignments__` on exactly the supplied
 * eligible date keys plus their per-employee detail maps, preserving other
 * employees, unit metadata, suppressions and sentinels. Never mutates.
 */
export function removeEmployeeAssignmentsFromOverrides(
  overrides: PlanInstanceOverrides,
  employeeIds: readonly string[],
  eligibleDateKeys: readonly string[]
): PlanInstanceOverrides {
  if (!isPlainRecord(overrides) || (employeeIds?.length ?? 0) === 0 || (eligibleDateKeys?.length ?? 0) === 0) return overrides;
  const targets = new Set(employeeIds);
  let result = overrides;
  for (const dateKey of new Set(eligibleDateKeys)) {
    if (dateKey === ASSIGNMENT_OVERRIDES_KEY || dateKey === DEFAULT_SUPPRESSIONS_KEY) continue;
    const dateEntry = result[dateKey];
    if (!isPlainRecord(dateEntry)) continue;
    const typedEntry = dateEntry as unknown as Record<string, ShiftUnitOverride>;
    const nextEntry = removeTargetsFromDateEntry(typedEntry, targets);
    if (nextEntry !== typedEntry) {
      if (result === overrides) result = { ...overrides };
      result = { ...result, [dateKey]: nextEntry };
    }
  }
  return result;
}

const EMPLOYEE_DETAIL_KEYS = ['employeeOverrides', 'assignmentTimes', 'assignmentLocations'] as const;

function withoutTargetKeys<T>(record: Record<string, T>, targets: Set<string>): Record<string, T> {
  if (!Object.keys(record).some(key => targets.has(key))) return record;
  const filtered: Record<string, T> = {};
  for (const [key, value] of Object.entries(record)) if (!targets.has(key)) filtered[key] = value;
  return filtered;
}

function removeTargetsFromUnitOverride(unit: Record<string, unknown>, targets: Set<string>): Record<string, unknown> {
  let next = unit;
  for (const key of EMPLOYEE_DETAIL_KEYS) {
    const detail = next[key];
    if (!isPlainRecord(detail)) continue;
    const filtered = withoutTargetKeys(detail, targets);
    if (filtered === detail) continue;
    if (next === unit) next = { ...unit };
    if (Object.keys(filtered).length === 0) {
      const pruned = { ...next };
      delete pruned[key];
      next = pruned;
    } else {
      next = { ...next, [key]: filtered };
    }
  }
  return next;
}

function removeTargetsFromDateEntry(dateEntry: Record<string, ShiftUnitOverride>, targets: Set<string>): Record<string, ShiftUnitOverride> {
  let next = dateEntry;
  const raw = (dateEntry as Record<string, unknown>)[ASSIGNMENT_OVERRIDES_KEY];
  if (isAssignmentOverrides(raw)) {
    const filtered = withoutTargetKeys(raw, targets);
    if (filtered !== raw) next = { ...next, [ASSIGNMENT_OVERRIDES_KEY]: filtered } as Record<string, ShiftUnitOverride>;
  }
  for (const unitId of Object.keys(dateEntry)) {
    if (unitId === ASSIGNMENT_OVERRIDES_KEY || unitId === DEFAULT_SUPPRESSIONS_KEY) continue;
    const unit = dateEntry[unitId];
    if (!isPlainRecord(unit)) continue;
    const cleaned = removeTargetsFromUnitOverride(unit, targets);
    if (cleaned === (unit as unknown as Record<string, unknown>)) continue;
    if (next === dateEntry) next = { ...dateEntry };
    if (Object.keys(cleaned).length === 0) {
      const pruned = { ...next };
      delete pruned[unitId];
      next = pruned;
    } else {
      next = { ...next, [unitId]: cleaned as ShiftUnitOverride };
    }
  }
  return next;
}

/**
 * Check if a plan instance has any meaningful overrides.
 * Returns true if at least one date key contains non-empty shift-unit overrides
 * or a non-empty __assignments__ map.
 */
export function hasOverrides(overrides: PlanInstanceOverrides | null | undefined): boolean {
  if (!overrides) return false;

  return Object.keys(overrides).some(dateKey => hasNonEmptyDateOverride(overrides[dateKey]));
}

/**
 * Checks whether a single date's override map contains any meaningful entries.
 */
function hasNonEmptyDateOverride(dateOverride: Record<string, ShiftUnitOverride> | undefined): boolean {
  if (!dateOverride) return false;

  return Object.keys(dateOverride).some(key => hasNonEmptyOverrideEntry(dateOverride, key));
}

/**
 * Checks whether a single override entry (assignment or unit) is non-empty.
 */
function hasNonEmptyOverrideEntry(dateOverride: Record<string, ShiftUnitOverride>, key: string): boolean {
  if (key === ASSIGNMENT_OVERRIDES_KEY) {
    const assignments = dateOverride[key] as unknown as AssignmentOverrides;
    return !!assignments && Object.keys(assignments).length > 0;
  }

  const unitOverride = dateOverride[key];
  return !!unitOverride && Object.keys(unitOverride).length > 0;
}

/**
 * Query parameters for fetching plan instances.
 */
export const planInstanceQuerySchema = type({
  planId: 'string',
  'startDate?': 'string', // Filter instances overlapping this date
  'endDate?': 'string', // Filter instances overlapping this date
  'status?': 'string', // Filter by status (draft | published)
});

export type PlanInstanceQuery = typeof planInstanceQuerySchema.t;

/**
 * Create schema for plan instances.
 *
 * Two modes:
 * - **Plan-backed** — `planId` present. `planVersionId` is optional provenance
 *   for one-time materialization; runtime assignments come from overrides.
 * - **Standalone** — `planId` absent. The instance is anchored only by
 *   `strategyId` and any per-date overrides.
 *
 * A plan-backed instance may intentionally have no source version. This is the
 * normal shape for an empty instance created before an explicit week-header
 * PlanVersion copy.
 */
export const createPlanInstanceSchema = type({
  strategyId: type('string>=1').configure({
    description: 'Required. Strategy that provides the shift-unit catalogue for this instance.',
    examples: ['strat_abc123'],
  }),
  'planId?': type('string>=1').configure({
    description: 'Optional source plan. Its selected version is copied into concrete instance state during creation/apply.',
    examples: ['plan_abc123'],
  }),
  'planVersionId?': type('string>=1').configure({
    description: 'Optional provenance for an explicit one-time source copy. Never used as a runtime fallback.',
    examples: ['planver_abc123'],
  }),
  startDate: type('string').configure({
    description: 'Start date of the instance as an ISO 8601 calendar date (YYYY-MM-DD).',
    examples: ['2026-04-20'],
  }),
  endDate: type('string').configure({
    description: 'End date of the instance as an ISO 8601 calendar date (YYYY-MM-DD).',
    examples: ['2026-04-26'],
  }),
  'timezone?': type('string').configure({
    description: "IANA timezone identifier used when expanding the weekday template into concrete dates. Defaults to 'UTC'.",
    examples: ['Europe/Zurich'],
  }),
  'overrides?': planInstanceOverridesSchema.configure({
    description: 'Optional per-date overrides (closures, capacity changes, replacement assignments) keyed by ISO date.',
  }),
  'status?': type("'draft' | 'published'").configure({
    description: "Lifecycle status. 'draft' is editable, 'published' is active. Defaults to 'draft'.",
    examples: ['draft'],
  }),
}).configure({
  description: 'Payload for creating a new plan instance. Plan-backed or standalone; source data is copied only when explicitly supplied.',
});

export type CreatePlanInstance = typeof createPlanInstanceSchema.t;

/**
 * Update schema for plan instances.
 */
export const updatePlanInstanceSchema = type({
  baseInstanceUpdatedAt: type('string').configure({
    description:
      "Optimistic-concurrency token. Must match the instance's current `updatedAt` ISO timestamp — the value the client read when the editing session began. Server returns 409 with a `currentUpdatedAt:<iso>` structured error entry when stale.",
    examples: ['2026-04-06T08:00:00.000Z'],
  }),
  'planVersionId?': type('string').configure({
    description:
      'Record or replace source-version provenance; assignments are stored on the instance and never read from this field at runtime.',
    examples: ['planver_abc123'],
  }),
  'startDate?': type('string').configure({
    description: 'Updated ISO 8601 start date (YYYY-MM-DD).',
    examples: ['2026-04-20'],
  }),
  'endDate?': type('string').configure({
    description: 'Updated ISO 8601 end date (YYYY-MM-DD).',
    examples: ['2026-04-26'],
  }),
  'timezone?': type('string').configure({
    description: 'Updated IANA timezone identifier.',
    examples: ['Europe/Zurich'],
  }),
  'overrides?': planInstanceOverridesSchema.configure({
    description: 'Replace the per-date overrides map for this instance.',
  }),
  'overridesPatch?': planInstanceOverridesPatchSchema,
  'status?': type("'draft' | 'published'").configure({
    description: "Updated lifecycle status. 'draft' (editable) or 'published' (active).",
    examples: ['published'],
  }),
})
  .narrow((value, ctx) => {
    if (value.overrides !== undefined && value.overridesPatch !== undefined) {
      return ctx.mustBe('either overrides (full replace) or overridesPatch (merge), not both');
    }
    return true;
  })
  .configure({
    description:
      'Partial update of a plan instance — fields omitted from the body remain unchanged. `baseInstanceUpdatedAt` is required for optimistic concurrency.',
  });

export type UpdatePlanInstance = typeof updatePlanInstanceSchema.t;

export const planPeriodBaselineSchema = type({
  strategy: type("'template' | 'copy' | 'empty'").configure({
    description:
      "Baseline source used to seed the new planning period. 'template' starts from the plan version template; 'copy' duplicates an existing instance; 'empty' starts blank.",
    examples: ['template'],
  }),
  'planVersionId?': type('string').configure({
    description: "Plan version used when strategy is 'template'. Required for that strategy.",
    examples: ['planver_abc123'],
  }),
  'sourceInstanceId?': type('string').configure({
    description: "Source plan instance used when strategy is 'copy'. Required for that strategy.",
    examples: ['planinst_abc123'],
  }),
}).configure({ description: 'Baseline specification describing how a new plan period is seeded.' });

export type PlanPeriodBaseline = typeof planPeriodBaselineSchema.t;

export const createPlanPeriodSchema = type({
  planId: type('string').configure({
    description: 'ID of the plan for which the period is created.',
    examples: ['plan_abc123'],
  }),
  startDate: type('string').configure({
    description: 'Start date of the period as an ISO 8601 calendar date (YYYY-MM-DD).',
    examples: ['2026-04-20'],
  }),
  endDate: type('string').configure({
    description: 'End date of the period as an ISO 8601 calendar date (YYYY-MM-DD).',
    examples: ['2026-04-26'],
  }),
  'timezone?': type('string').configure({
    description: 'IANA timezone identifier for date expansion.',
    examples: ['Europe/Zurich'],
  }),
  'baseline?': planPeriodBaselineSchema.configure({
    description: 'Optional baseline specifying how the new period is pre-populated.',
  }),
}).configure({ description: 'Payload for creating a direct planning period (period-mode plan instance).' });

export type CreatePlanPeriod = typeof createPlanPeriodSchema.t;

export const planPeriodQuerySchema = type({
  planId: 'string',
  startDate: 'string',
  endDate: 'string',
});

export type PlanPeriodQuery = typeof planPeriodQuerySchema.t;

export const PLAN_VERSION_APPLY_MODES = ['merge', 'overwrite'] as const;

export type PlanVersionApplyMode = (typeof PLAN_VERSION_APPLY_MODES)[number];

export const applyPlanVersionSchema = type({
  planVersionId: type('string').configure({
    description: 'ID of the plan version to apply to the target week.',
    examples: ['planver_abc123'],
  }),
  targetWeekStartDate: type('string').configure({
    description: 'ISO 8601 date (YYYY-MM-DD) of the Monday the version should be applied to.',
    examples: ['2026-04-20'],
  }),
  mode: type("'merge' | 'overwrite'").configure({
    description:
      "Apply mode. 'merge' keeps existing assignments and fills gaps; 'overwrite' replaces the target week's assignments entirely.",
    examples: ['merge'],
  }),
  baseInstanceUpdatedAt: type('string').configure({
    description: 'ISO 8601 timestamp of the plan instance `updatedAt` fingerprint for stale-data detection (optimistic concurrency).',
    examples: ['2026-04-22T08:00:00.000Z'],
  }),
}).configure({ description: 'Payload for applying a plan version template to a specific week of a plan instance.' });

export type ApplyPlanVersion = typeof applyPlanVersionSchema.t;

/**
 * Query parameters for schedule expansion.
 */
export const scheduleQuerySchema = type({
  'startDate?': 'string', // Override start (default: instance.startDate)
  'endDate?': 'string', // Override end (default: instance.endDate)
});

export type ScheduleQuery = typeof scheduleQuerySchema.t;
